import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, RefreshCw, ShieldCheck } from 'lucide-react';

import { ApiError, request } from '@/api/client';
import type { ConsentContext, ConsentDetail, MarketingConsent } from '@/api/marketingConsent';
import type { Page } from '@/api/types';
import { PageHeader } from '@/components/AppShell';
import { Pagination } from '@/components/DataTable';
import { Badge, Button, EmptyState, ErrorState, Field, Modal, Select, TextArea, TextInput } from '@/components/ui';
import { useResource } from '@/lib/hooks';
import { dateTime } from '@/lib/format';
import { useAuth } from '@/auth/AuthContext';
import { emptyMarketingValues, marketingDraftKey, readMarketingDraft } from '@/lib/marketingDraft';

const BASE = '/api/v1/admin/marketing-consents';

function useRefreshWhileVisible(reload: () => void) {
  useEffect(() => {
    const refresh = () => { if (document.visibilityState === 'visible') reload(); };
    const timer = window.setInterval(refresh, 30000);
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [reload]);
}

function StateBadge({ item }: { item: MarketingConsent }) {
  const { t } = useTranslation();
  return <Badge tone={item.can_use ? 'positive' : item.status === 'revoked' ? 'critical' : 'neutral'}>
    {t(`marketing.status.${item.status}`)}
  </Badge>;
}

function errorKey(error: unknown): string {
  return error instanceof ApiError && error.message.startsWith('marketing_')
    ? `marketing.errors.${error.message}` : 'marketing.errors.failed';
}

export function MarketingConsentsPage() {
  const { t, i18n } = useTranslation();
  const [params] = useSearchParams();
  const athlete = params.get('athlete');
  return <ConsentList key={athlete ?? 'all'} athlete={athlete} language={i18n.language} t={t} />;
}

function ConsentList({ athlete, language, t }: { athlete: string | null; language: string; t: ReturnType<typeof useTranslation>['t'] }) {
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<string | null>(null);
  const { data, loading, error, reload } = useResource<Page<MarketingConsent>>(BASE, { athlete_id: athlete, page, page_size: 20 }, [athlete, page]);
  useRefreshWhileVisible(reload);
  const close = useCallback(() => { setSelected(null); reload(); }, [reload]);

  return <>
    <PageHeader title={t('marketing.title')} subtitle={t('marketing.subtitle')}
      actions={<div className="flex flex-wrap gap-2">
        {!athlete && <Link className="inline-flex min-h-9 items-center rounded-lg bg-brand px-3 text-sm font-medium text-on-brand hover:bg-brand-strong" to="/people">{t('marketing.newRequest')}</Link>}
        <Button icon={<RefreshCw className="h-4 w-4" />} onClick={reload}>{t('marketing.refresh')}</Button>
      </div>} />
    {athlete && <div className="mb-5 flex flex-wrap gap-4 text-sm">
      <Link className="text-brand underline" to="/people">{t('marketing.choosePerson')}</Link>
      <Link className="text-brand underline" to="/marketing-consents">{t('marketing.allRequests')}</Link>
    </div>}
    {athlete && <NewRequest athleteId={athlete} onCreated={(item) => { setPage(1); reload(); setSelected(item.id); }} />}
    <div className="card" aria-busy={loading}>
      <div className="border-b border-line p-4">
        <h2 className="font-semibold">{t('marketing.history')}</h2>
        <p className="mt-1 text-xs text-ink-subtle">{t('marketing.refreshHint')}</p>
      </div>
      {error ? <ErrorState message={t('common.loadFailed')} onRetry={reload} /> : loading && !data ?
        <p role="status" className="p-6 text-sm text-ink-subtle">{t('common.loading')}</p> : data?.items.length ? <>
          <ul className="divide-y divide-line">
            {data.items.map((item) => <li key={item.id}>
              <button className="flex w-full flex-col gap-3 p-4 text-left hover:bg-surface-sunken sm:flex-row sm:items-start sm:justify-between"
                onClick={() => setSelected(item.id)}>
                <div className="min-w-0">
                  <p className="break-words font-medium">{item.title}</p>
                  <p className="mt-1 text-sm text-ink-muted">{item.athlete_name}</p>
                  <p className="mt-1 text-xs text-ink-subtle">{t('marketing.updated', { date: dateTime(item.updated_at, language) })}</p>
                  {!item.email_sent && item.stored_status === 'pending' && <p className="mt-2 text-sm text-danger">{t('marketing.mailFailed')}</p>}
                  {item.status === 'revoked' && <p className="mt-2 text-sm text-danger">{t('marketing.reviewPublished')}</p>}
                </div>
                <div className="flex shrink-0 flex-col items-start gap-2 sm:items-end">
                  {loading ? <span className="text-xs text-ink-subtle">{t('marketing.checking')}</span> : <StateBadge item={item} />}
                  <span className="text-xs text-ink-subtle">{t('marketing.details')}</span>
                </div>
              </button>
            </li>)}
          </ul>
          <Pagination page={page} pageSize={20} total={data.total} onPage={setPage} />
        </> : <EmptyState icon={<ShieldCheck className="h-8 w-8" />} title={t('marketing.emptyTitle')} body={t(athlete ? 'marketing.emptyForPerson' : 'marketing.emptyBody')} />}
    </div>
    {selected && <RequestDetail id={selected} onClose={close} onChange={reload} />}
  </>;
}

function NewRequest({ athleteId, onCreated }: { athleteId: string; onCreated: (item: MarketingConsent) => void }) {
  const { t } = useTranslation();
  const { data, error, loading, reload } = useResource<ConsentContext>(`/api/v1/admin/people/${athleteId}/marketing-consent-context`, undefined, [athleteId]);
  const [formKey, setFormKey] = useState(0);
  return <section className="card mb-6 p-5">
    <h2 className="text-base font-semibold">{t('marketing.newRequest')}</h2>
    {error ? <ErrorState message={t(errorKey(new ApiError(409, error)))} onRetry={reload} /> : loading ?
      <p role="status" className="mt-4 text-sm">{t('common.loading')}</p> : data && <>
        <p className="mt-1 font-medium">{data.athlete_name}</p>
        {data.birth_date_required ? <p role="alert" className="mt-4 text-danger">{t('marketing.errors.marketing_birth_date_required')}</p> :
          <RequestForm key={formKey} context={data} onCreated={(item) => { setFormKey((n) => n + 1); onCreated(item); }} />}
      </>}
  </section>;
}

const fields = [
  { key: 'purpose', max: 2000 }, { key: 'channels', max: 1000 },
  { key: 'materials', max: 2000 }, { key: 'additional_details', max: 2000 },
] as const;

function RequestForm({ context, onCreated }: { context: ConsentContext; onCreated: (item: MarketingConsent) => void }) {
  const { t, i18n } = useTranslation();
  const { account } = useAuth();
  const draftKey = marketingDraftKey(account!.id, context.athlete_id);
  const [saved] = useState(() => readMarketingDraft(draftKey));
  const [id] = useState(() => saved?.id ?? crypto.randomUUID());
  const [values, setValues] = useState(saved?.values ?? emptyMarketingValues);
  const [recipient, setRecipient] = useState(saved?.recipient ?? '');
  const [locale, setLocale] = useState(saved?.locale ?? (i18n.language === 'en' ? 'en' : 'de'));
  const [busy, setBusy] = useState(false);
  const [uncertain, setUncertain] = useState(saved?.attempted ?? false);
  const [storageAvailable, setStorageAvailable] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const email = context.recipient_email ?? recipient;
  const dirty = uncertain || !!recipient.trim() || Object.values(values).some((value) => !!value.trim());

  useEffect(() => {
    try {
      if (dirty) sessionStorage.setItem(draftKey, JSON.stringify({ id, values, recipient, locale, attempted: uncertain }));
      else sessionStorage.removeItem(draftKey);
      setStorageAvailable(true);
    } catch { setStorageAvailable(false); }
  }, [draftKey, id, values, recipient, locale, uncertain, dirty]);

  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  function completed(item: MarketingConsent) {
    try { sessionStorage.removeItem(draftKey); } catch { /* Draft storage may be unavailable. */ }
    onCreated(item);
  }

  async function reconcile(): Promise<boolean> {
    try {
      const saved = await request<MarketingConsent>(`${BASE}/${id}`);
      completed(saved);
      return true;
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 404) { setUncertain(false); return false; }
      setUncertain(true);
      setError('marketing.uncertain');
      return true;
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true); setError(null);
    try {
      if (uncertain) { await reconcile(); return; }
      const trimmed = Object.fromEntries(Object.entries(values).map(([key, value]) => [key, value.trim()]));
      if (['title', 'purpose', 'channels', 'materials'].some((key) => !trimmed[key])) {
        setError('marketing.required'); return;
      }
      setUncertain(true);
      const saved = await request<MarketingConsent>(`/api/v1/admin/people/${context.athlete_id}/marketing-consents`, {
        method: 'POST', body: { ...trimmed, request_id: id, recipient_email: email, locale },
      });
      completed(saved);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status < 500) {
        setUncertain(caught.message === 'marketing_request_changed');
        setError(errorKey(caught));
      }
      else if (!(await reconcile())) setError('marketing.errors.failed');
    } finally { setBusy(false); }
  }

  return <form onSubmit={submit} className="mt-5 space-y-5">
    <p className="text-sm text-ink-muted">{t('marketing.formHint')}</p>
    {dirty && <p role="status" className="text-xs text-ink-subtle">{t(storageAvailable ? 'marketing.draftSaved' : 'marketing.draftUnavailable')}</p>}
    {uncertain && !busy && !error && <p role="status" className="text-sm text-ink-muted">{t('marketing.uncertain')}</p>}
    <fieldset disabled={busy || uncertain} className="space-y-5">
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label={t(`marketing.recipient.${context.recipient_role}`)} hint={t(context.recipient_locked ? 'marketing.recipientLocked' : 'marketing.guardianHint')}>
          {(fieldId, describedBy) => <TextInput id={fieldId} aria-describedby={describedBy} type="email" required maxLength={255}
            readOnly={context.recipient_locked} value={email} onChange={(event) => setRecipient(event.target.value)} />}
        </Field>
        <Field label={t('marketing.language')} hint={t('marketing.languageHint')}>
          {(fieldId, describedBy) => <Select id={fieldId} aria-describedby={describedBy} value={locale} onChange={(event) => setLocale(event.target.value)}>
            <option value="de">Deutsch</option><option value="en">English</option>
          </Select>}
        </Field>
      </div>
      <Field label={t('marketing.fields.title')} hint={t('marketing.hints.title')}>
        {(fieldId, describedBy) => <TextInput id={fieldId} aria-describedby={describedBy} required maxLength={160} value={values.title}
          onChange={(event) => setValues({ ...values, title: event.target.value })} />}
      </Field>
      <div className="grid gap-5 lg:grid-cols-2">
        {fields.map(({ key, max }) => <Field key={key} label={t(`marketing.fields.${key}`)} hint={t(`marketing.hints.${key}`)}>
          {(fieldId, describedBy) => <TextArea id={fieldId} aria-describedby={describedBy} required={key !== 'additional_details'}
            maxLength={max} value={values[key]} onChange={(event) => setValues({ ...values, [key]: event.target.value })} />}
        </Field>)}
      </div>
    </fieldset>
    {error && <p role="alert" className="text-sm text-danger">{t(error)}</p>}
    <div className="flex flex-wrap items-center gap-4">
      <Button type="submit" variant="primary" loading={busy} icon={<Mail className="h-4 w-4" />}>{t(busy ? 'marketing.working' : uncertain ? 'marketing.checkSaved' : 'marketing.send')}</Button>
      <p className="max-w-xl text-xs text-ink-subtle">{t('marketing.sendHint')}</p>
    </div>
  </form>;
}

function RequestDetail({ id, onClose, onChange }: { id: string; onClose: () => void; onChange: () => void }) {
  const { t, i18n } = useTranslation();
  const { data, error, loading, reload } = useResource<ConsentDetail>(`${BASE}/${id}`, undefined, [id]);
  useRefreshWhileVisible(reload);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [confirmStop, setConfirmStop] = useState(false);

  async function act(action: 'resend' | 'cancel') {
    if (busy) return;
    setBusy(true); setActionError(null);
    try {
      await request(`${BASE}/${id}/${action}`, { method: 'POST' });
      setConfirmStop(false); reload(); onChange();
    } catch (caught) { setActionError(errorKey(caught)); reload(); onChange(); }
    finally { setBusy(false); }
  }

  return <Modal open onClose={onClose} title={t('marketing.details')} description={data?.athlete_name}>
    {error ? <ErrorState message={t('common.loadFailed')} onRetry={reload} /> : loading && !data ?
      <p role="status">{t('common.loading')}</p> : data && <div className="space-y-5">
        <div><h2 className="mb-3 break-words text-base font-semibold">{data.title}</h2>{loading ? <p role="status" className="text-sm text-ink-subtle">{t('marketing.checking')}</p> : <StateBadge item={data} />}</div>
        <p className={`text-sm ${data.can_use ? 'text-ink' : 'text-danger'}`}>
          {t(loading ? 'marketing.checking' : data.can_use ? 'marketing.approvedHint' : 'marketing.noPermission')}
        </p>
        {data.status === 'revoked' && <p className="rounded-lg border border-line p-3 text-sm text-danger">{t('marketing.reviewPublished')}</p>}
        {!data.email_sent && ['pending', 'approved', 'expired'].includes(data.status) && <p role="alert" className="text-sm text-danger">{t('marketing.mailFailed')}</p>}
        <dl className="space-y-4 text-sm">
          <div><dt className="text-ink-subtle">{t(`marketing.recipient.${data.recipient_role}`)}</dt><dd className="break-all">{data.recipient_email || t('marketing.contactRemoved')}</dd></div>
          {fields.map(({ key }) => data[key] ? <div key={key}><dt className="font-medium">{t(`marketing.fields.${key}`)}</dt><dd className="mt-1 whitespace-pre-wrap break-words text-ink-muted">{data[key]}</dd></div> : null)}
          <div><dt className="font-medium">{t('marketing.exactText')}</dt><dd className="mt-1 whitespace-pre-wrap text-ink-muted">{data.consent_text}</dd></div>
          {data.stored_status === 'pending' && <div><dt className="font-medium">{t('marketing.expires')}</dt><dd>{dateTime(data.expires_at, i18n.language)}</dd></div>}
        </dl>
        <div className="flex flex-wrap gap-2">
          {['pending', 'expired', 'approved'].includes(data.status) && <Button disabled={loading} loading={busy} onClick={() => act('resend')}>{t(data.status === 'approved' ? 'marketing.resendManagement' : 'marketing.resend')}</Button>}
          {['pending', 'approved'].includes(data.stored_status) && data.status !== 'unavailable' && <Button disabled={busy || loading} onClick={() => setConfirmStop(true)}>{t(data.stored_status === 'approved' ? 'marketing.stopUse' : 'marketing.cancelRequest')}</Button>}
          <Button disabled={busy || loading} onClick={reload}>{t('marketing.refresh')}</Button>
        </div>
        {confirmStop && <div className="rounded-lg border border-line p-3">
          <p className="mb-3 text-sm">{t(data.stored_status === 'approved' ? 'marketing.stopConfirm' : 'marketing.cancelConfirm')}</p>
          <div className="flex flex-wrap gap-2"><Button variant="danger" disabled={loading} loading={busy} onClick={() => act('cancel')}>{t(data.stored_status === 'approved' ? 'marketing.stopUse' : 'marketing.cancelRequest')}</Button><Button disabled={busy} onClick={() => setConfirmStop(false)}>{t('common.cancel')}</Button></div>
        </div>}
        {actionError && <p role="alert" className="text-sm text-danger">{t(actionError)}</p>}
        <div className="border-t border-line pt-4">
          <h3 className="mb-3 text-sm font-semibold">{t('marketing.eventsTitle')}</h3>
          <ol className="space-y-3 text-sm">{data.events.map((event, index) => <li key={`${event.occurred_at}-${index}`}>
            <p>{t(`marketing.events.${event.action}`)} · {t(`marketing.actors.${event.actor}`)}</p>
            <p className="text-xs text-ink-subtle">{dateTime(event.occurred_at, i18n.language)}</p>
          </li>)}</ol>
          <p className="mt-4 break-all text-xs text-ink-subtle">{data.id} · {data.consent_version}</p>
        </div>
      </div>}
  </Modal>;
}
